/**
 * Permission Service — Client-side runtime adapter.
 * ==================================================
 *
 * Thin fetch/cache wrapper around the Supabase queries that powers
 * every UI permission gate. The PURE engine in
 * src/core/permissions/engine.ts is the source of truth for the rules;
 * this service just supplies the `userRoles` array.
 *
 * Usage:
 *   const { roles, refresh, hasPerm, loading } = useVaultPermissions(activeVault?.id);
 *   if (hasPerm(PERMISSION_KICK_MEMBERS)) openKickDialog();
 */

import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../supabase';
import {
  hasPermission as engineHasPermission,
  hasAnyPermission as engineHasAny,
  type AnyRole,
} from '../core/permissions/engine';
export { PermissionService, usePermission } from './PermissionService';

const ROLES_CACHE = new Map<string, { roles: AnyRole[]; at: number }>();
const CACHE_TTL_MS = 30_000;

async function fetchVaultRoles(vaultId: string | null | undefined): Promise<AnyRole[]> {
  if (!vaultId) return [];
  const cached = ROLES_CACHE.get(vaultId);
  if (cached && Date.now() - cached.at < CACHE_TTL_MS) return cached.roles;

  const { data: user } = await supabase.auth.getUser();
  if (!user.user) return [];

  const [{ data: allRoles, error: rolesErr }, { data: memberRoles, error: memberErr }] =
    await Promise.all([
      supabase.from('server_roles').select('id, name, position, vault_id, role_kind, permission_ids').eq('vault_id', vaultId),
      supabase.from('server_member_roles').select('role_id')
        .eq('vault_id', vaultId).eq('user_id', user.user.id),
    ]);

  if (rolesErr || memberErr || !allRoles) {
    // Quiet warning if migration tables aren't present in DB schema yet
    return cached?.roles ?? [];
  }

  const assignedIds = new Set((memberRoles ?? []).map(r => r.role_id));
  const roles = (allRoles as AnyRole[]).filter(r => assignedIds.has(r.id));

  ROLES_CACHE.set(vaultId, { roles, at: Date.now() });
  return roles;
}

/**
 * useVaultPermissions — primary hook for any component that needs to
 * gate UI behind a permission. Re-fetches on vault change + every 60s.
 */
export function useVaultPermissions(vaultId: string | null | undefined) {
  const [roles, setRoles] = useState<AnyRole[]>([]);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!vaultId) { setRoles([]); return; }
    setLoading(true);
    const r = await fetchVaultRoles(vaultId);
    setRoles(r);
    setLoading(false);
  }, [vaultId]);

  useEffect(() => { void refresh(); }, [refresh]);

  useEffect(() => {
    if (!vaultId) return;
    const id = window.setInterval(() => { void refresh(); }, 60_000);
    return () => window.clearInterval(id);
  }, [vaultId, refresh]);

  const hasPerm = useCallback(
    (permission: string) => engineHasPermission(roles, permission),
    [roles],
  );
  const hasAny = useCallback(
    (permissions: string[]) => engineHasAny(roles, permissions),
    [roles],
  );

  return { roles, loading, refresh, hasPerm, hasAny };
}

export interface AuditLogEntry {
  id: string;
  vault_id: string;
  actor_id: string;
  action: string;
  target_id: string | null;
  reason: string | null;
  metadata: Record<string, unknown>;
  created_at: string;
}

/**
 * fetchAuditLog — reads the moderation history for a vault, gated
 * server-side by VIEW_AUDIT_LOG via /api/audit-log.
 */
export async function fetchAuditLog(
  vaultId: string,
  limit = 50,
): Promise<AuditLogEntry[]> {
  const url = new URL('/api/audit-log', window.location.origin);
  url.searchParams.set('vaultId', vaultId);
  url.searchParams.set('limit', String(limit));
  const sess = (await supabase.auth.getSession()).data.session;
  const res = await fetch(url.toString(), {
    headers: sess?.access_token
      ? { Authorization: `Bearer ${sess.access_token}` }
      : undefined,
  });
  if (!res.ok) return [];
  const body: { entries?: AuditLogEntry[] } = await res.json().catch(() => ({}));
  return body.entries ?? [];
}
